/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';

/**
 * Supported file types for upload
 */
const SUPPORTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'text/plain': ['.txt'],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * FileUploadZone Component
 * Drag & drop file upload zone with validation using react-dropzone
 */
function FileUploadZone({ onFilesSelected, selectedFiles, onRemoveFile }) {
  const [error, setError] = useState('');

  const validateFile = (file) => {
    // Check file type
    if (!Object.keys(SUPPORTED_FILE_TYPES).includes(file.type)) {
      return `File type not supported: ${file.name}`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (max 10MB): ${file.name}`;
    }

    return null;
  };

  const handleDrop = (acceptedFiles) => {
    setError('');
    const errors = [];

    const validFiles = acceptedFiles.filter((file) => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
        return false;
      }
      return true;
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    }

    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: Object.keys(SUPPORTED_FILE_TYPES).reduce((acc, key) => {
      acc[key] = SUPPORTED_FILE_TYPES[key];
      return acc;
    }, {}),
    multiple: true,
    maxSize: MAX_FILE_SIZE,
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / (k ** i)) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <div>
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? '#0d6efd' : '#ccc'}`,
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
          transition: 'all 0.2s ease',
        }}
      >
        <input {...getInputProps()} />
        <div>
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ margin: '0 auto 16px', color: '#6c757d' }}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          {isDragActive ? (
            <p style={{ margin: '0 0 8px', fontWeight: '500' }}>
              Drop the files here...
            </p>
          ) : (
            <>
              <p style={{ margin: '0 0 8px', fontWeight: '500' }}>
                Drag & drop files here, or click to select
              </p>
              <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
                Supported: PDF, DOCX, XLSX, JPG, PNG, TXT (max 10MB per file)
              </p>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {selectedFiles && selectedFiles.length > 0 && (
        <div className="mt-3">
          <h6>Selected Files:</h6>
          <ul className="list-group">
            {selectedFiles.map((file, index) => (
              <li
                key={`file-${file.name}-${file.size}-${file.lastModified}`}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{file.name}</strong>
                  <small className="text-muted ms-2">
                    (
                    {formatFileSize(file.size)}
                    )
                  </small>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onRemoveFile(index)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

FileUploadZone.propTypes = {
  onFilesSelected: PropTypes.func.isRequired,
  selectedFiles: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  onRemoveFile: PropTypes.func.isRequired,
};

FileUploadZone.defaultProps = {
  selectedFiles: [],
};

export default FileUploadZone;
